import Service from '@ember/service';
import { assert } from '@ember/debug';
import { tracked } from '@glimmer/tracking';
import * as PrettyNum from '@ebiz/pretty-num';
import { sigFigsDown } from 'simdds/utils';
import Owner from '@ember/owner';

export interface ProductRow {
  name: string;
  masterFclk: number;
  resolution: number;
  tuningWordWidth: number;
  discardedBits: number;
  multipliers: number[] | null;
  refClkMinFreq: number | null;
  refClkMinFreqWMult: number | null;
  spurCoeff1: number | null;
  spurCoeff2: number | null;
  spurCoeff3: number | null;
  spurCoeff4: number | null;
  spurCoeff5: number | null;
  spurCoeff6: number | null;
}

// Pull out any unexpected query parameters for passing on
const getNonDDSQueryString = function () {
  const qsListItems = [];

  let qs = window.location.href;
  if (qs.indexOf('?') !== -1) {
    qs = qs.substring(qs.indexOf('?') + 1);
  } else {
    qs = '';
  }

  const qsList = qs.split('&');
  let qsReturn = '';

  for (const qsListItem of qsList) {
    const qsName = qsListItem.split('=')[0];
    switch (qsName) {
      case 'part':
      case 'fin':
      case 'sclock':
      case 'mult':
      case 'ftw':
      case 'tof':
      case 'rso':
      case 'harmonicDB':
      case 'useFilters':
      case 'fType':
      case 'fTop':
      case 'fOrder':
      case 'fc':
      case 'fRipple':
      case 'fAtten':
      case 'debug':
      case 'lang':
        break;
      default:
        qsListItems.push(qsListItem);
        break;
    }
  }
  // Rebuild URL
  const qsNonDDS = qsListItems;
  if (qsNonDDS.length > 0 && qsNonDDS[0] !== '') {
    qsReturn += '?';
    for (let i = 0; i < qsNonDDS.length; i++) {
      if (i > 0) {
        qsReturn += '&';
      }
      qsReturn += qsNonDDS[i];
    }
  }

  return qsReturn;
};

export default class StateService extends Service {
  _serviceState = 'Nascent';

  products: ProductRow[] = [];
  nonDDSQueryString: string;

  @tracked _suppliedParms: Record<string, string> = {};
  @tracked _referrerPartNumber?: string;

  @tracked _partData?: ProductRow;
  @tracked _finMoreRecentThanSClock?: boolean;
  @tracked _ftwMoreRecentThanTof?: boolean;

  @tracked _partNumber?: string;
  @tracked _inputClockFrequency?: string;
  @tracked _systemClock?: string;
  @tracked _multiplier?: string;
  @tracked _ftw?: string;
  @tracked _targetFrequency?: string;
  @tracked _signalDisplayOptions?: string;
  @tracked _harmonicDB?: string;

  @tracked _displayFilters?: boolean;
  @tracked _filterType?: string;
  @tracked _filterTopology?: string;
  @tracked _filterOrder?: string;
  @tracked _fc?: string;

  @tracked _passbandRipple?: string;
  @tracked _stopbandAtten?: string;
  @tracked _isDebug?: string;

  constructor(owner: Owner) {
    super(owner);
    this.nonDDSQueryString = '';
  }

  // Start-time setup
  start(
    products: ProductRow[],
    referrerPartNumber: string | undefined,
    suppliedParameters: Record<string, string>,
  ): void {
    this.products = products;
    this.nonDDSQueryString = getNonDDSQueryString();
    for (const [key, value] of Object.entries(suppliedParameters)) {
      if (value) {
        this._suppliedParms[key] = value;
      }
    }
    this._referrerPartNumber = referrerPartNumber;
    this.setInitialParams();
    // This class is now structurally safe to fully use.
    this._serviceState = 'Ready';
  }

  setInitialParams(): void {
    this._partNumber =
      this._validatedPart(this._suppliedParms['partId']) ?? this._validatedPart(this._referrerPartNumber) ?? 'AD9914';
    this._partData = this.products.find((item) => {
      return item.name === this._partNumber;
    });
    this._multiplier = this._negotiateMultiplier(
      this._suppliedParms['qmult'],
      this._suppliedParms['qsclock'],
      this._suppliedParms['qfin'],
    );
    this._systemClock = this._negotiateSystemClockFrequency(
      this._multiplier,
      this._suppliedParms['qsclock'],
      this._suppliedParms['qfin'],
    );
    this._inputClockFrequency = this._negotiateClockFrequency(
      this._multiplier,
      this._systemClock,
      this._suppliedParms['qfin'],
    );
    this._targetFrequency = this._negotiateTof(
      this._inputClockFrequency,
      this._systemClock,
      this._suppliedParms['qftw'],
      this._suppliedParms['qtof'],
    );
    this._ftw = this._negotiateFtw(
      this._systemClock,
      this._inputClockFrequency,
      this._multiplier,
      this._suppliedParms['qtof'],
      this._suppliedParms['qftw'],
    );
    this._signalDisplayOptions = this._negotiateRso(this._suppliedParms['qrso']);
    this._harmonicDB = this._negotiateHarmonicDB(this._suppliedParms['harmonicDB']);

    this._displayFilters = this._negotiateDisplayFilters(this._suppliedParms['useFilters']);
    this._filterType = this._negotiateFilterType(this._suppliedParms['qfType']);
    this._filterTopology = this._negotiateFilterTopology(this._suppliedParms['qfTop']);
    this._filterOrder = this._negotiateFilterOrder(this._suppliedParms['qfOrder']);
    this._fc = this._negotiateFc(this._suppliedParms['qfc']);

    this._passbandRipple = this._negotiatePassbandRipple(this._suppliedParms['qfRipple']);
    this._stopbandAtten = this._negotiateStopbandAtten(this._suppliedParms['qfAtten']);
    this._isDebug = this._negotiateIsDebug(this._suppliedParms['qdebug']);

    this._ftwMoreRecentThanTof = Object.keys(this._suppliedParms).find((x) => {
      return x === 'qftw';
    })
      ? true
      : false;
    this._finMoreRecentThanSClock = Object.keys(this._suppliedParms).find((x) => {
      return x === 'qfin';
    })
      ? true
      : false;
  }

  _validatedPart(partId: string | undefined): string | undefined {
    const partData = this.products.find((item) => {
      return !!partId && item.name === partId;
    });
    return partData?.name;
  }

  _ftwForTargetFrequency(
    systemClock: number,
    clockFrequency: number,
    multiplier: number,
    targetFrequency: number,
  ): number {
    const bitspace = Math.pow(2, this._partData ? this._partData.tuningWordWidth : 32);
    const sysClock = systemClock ?? clockFrequency * multiplier;
    return Math.round((targetFrequency * bitspace) / sysClock);
  }

  _negotiateMultiplier(mult?: string, sclock?: string, fin?: string): string {
    if (
      mult &&
      PrettyNum.userEntryToNumber(mult) > 1 &&
      this._partData &&
      this._partData.multipliers &&
      this._partData.multipliers.length > 0 &&
      this._partData.multipliers.includes(PrettyNum.userEntryToNumber(mult))
    ) {
      if (!sclock && !fin) {
        return mult;
      }
      if (sclock) {
        const numericSclock = PrettyNum.userEntryToNumber(sclock);
        const numericMult = PrettyNum.userEntryToNumber(mult);
        const inputClockFrequency = sigFigsDown(numericSclock / numericMult, 8);
        if (inputClockFrequency >= 2) {
          return mult;
        }
      }
      if (fin) {
        const numericFin = PrettyNum.userEntryToNumber(fin);
        if (numericFin >= 2) {
          return mult;
        }
      }
    }

    return '1';
  }

  _negotiateClockFrequency(mult: string, sclock: string, fin?: string): string {
    const defaultFin = '2';
    if (fin) {
      if (PrettyNum.userEntryToNumber(fin) >= 2) {
        return fin;
      }
    } else {
      const numericSystemClock = sclock ? PrettyNum.userEntryToNumber(sclock) : (this._partData?.masterFclk ?? 3.5e9);
      const numericMultiplier = PrettyNum.userEntryToNumber(mult);
      const clockInput = sigFigsDown(numericSystemClock / numericMultiplier, 8);
      return clockInput >= 2 ? PrettyNum.numberToUserDisplay(clockInput, 'Engineering', 8) : '2';
    }

    return defaultFin;
  }

  _negotiateSystemClockFrequency(mult: string, sclock?: string, fin?: string): string {
    const defaultSclock = '2';
    if (sclock) {
      if (PrettyNum.userEntryToNumber(sclock) >= 2) {
        return sclock;
      }
    } else {
      if (fin) {
        const sysClock = PrettyNum.userEntryToNumber(fin) * PrettyNum.userEntryToNumber(mult);
        if (sysClock >= 2) {
          return PrettyNum.numberToUserDisplay(sysClock, 'Engineering', 12);
        }
      } else {
        const masterFclk = this._partData?.masterFclk ?? 3.5e9;
        return PrettyNum.numberToUserDisplay(masterFclk, 'Engineering', 8);
      }
    }

    return defaultSclock;
  }

  _negotiateTof(fin: string, sclock: string, ftw?: string, tof?: string): string {
    if (tof) {
      return tof;
    } else if (ftw) {
      const tof =
        (parseInt(ftw, 16) * PrettyNum.userEntryToNumber(sclock)) / Math.pow(2, this._partData?.tuningWordWidth ?? 32);
      return PrettyNum.numberToUserDisplay(sigFigsDown(tof, 12), 'Engineering', 12);
    } else if (this._partNumber === 'AD9914') {
      return '1.28G'; // Only applies in the absence of explicit data
    } else {
      const systemClockInput = PrettyNum.userEntryToNumber(sclock);
      const defaultTof = systemClockInput * 0.38;
      return PrettyNum.numberToUserDisplay(sigFigsDown(defaultTof, 12), 'Engineering', 12);
    }
  }

  _negotiateFtw(sclock: string, fin: string, mult: string, tof?: string, suppliedFtw?: string): string {
    const numericSystemClock = PrettyNum.userEntryToNumber(sclock);
    const numericClockInput = PrettyNum.userEntryToNumber(fin);
    const numericMultiplier = PrettyNum.userEntryToNumber(mult);

    const masterFclk = this._partData?.masterFclk ?? 3.5e9;
    //Set limits for FTW
    const oneHzFtw = this._ftwForTargetFrequency(numericSystemClock, numericClockInput, numericMultiplier, 1);
    const maxHzFtw = this._ftwForTargetFrequency(
      numericSystemClock,
      numericClockInput,
      numericMultiplier,
      masterFclk / 2, // Nyquist limit
    );

    if (suppliedFtw) {
      return this._validateFtw(suppliedFtw, oneHzFtw, maxHzFtw);
    } else {
      if (numericClockInput * numericMultiplier > masterFclk) {
        return '';
      }

      const defaulttof = PrettyNum.numberToUserDisplay(sigFigsDown(numericSystemClock * 0.38, 12), 'Engineering', 12);
      const targetFreq = tof ? tof : this._partNumber === 'AD9914' ? '1.28G' : defaulttof;
      return this._validateFtw(
        this._ftwForTargetFrequency(
          numericSystemClock,
          numericClockInput,
          numericMultiplier,
          PrettyNum.userEntryToNumber(targetFreq),
        ).toString(16),
        oneHzFtw,
        maxHzFtw,
      );
    }
  }

  _negotiateRso(rso?: string): string {
    if (rso) {
      return rso;
    }
    return '111111';
  }

  _negotiateHarmonicDB(harmonicDB?: string): string {
    if (harmonicDB) {
      return harmonicDB;
    }
    return '-50';
  }

  _negotiateDisplayFilters(useFilter?: string): boolean {
    if (useFilter === '1' || useFilter === 'true') {
      return true;
    }
    return false;
  }

  _negotiateFilterTopology(ftop?: string): string {
    if (ftop) {
      return ftop;
    }
    return '0';
  }

  _negotiateFilterType(fType?: string): string {
    if (fType) {
      return fType;
    }
    return '0';
  }

  _negotiateFilterOrder(fOrder?: string): string {
    if (fOrder) {
      return fOrder;
    }
    return '1';
  }

  _negotiatePassbandRipple(fRipple?: string): string {
    if (fRipple) {
      return fRipple;
    }
    return '0.5';
  }

  _negotiateStopbandAtten(fAtten?: string): string {
    if (fAtten) {
      return fAtten;
    }
    return '60';
  }

  _negotiateFc(fc?: string): string {
    if (fc) {
      return fc;
    } else {
      const numericSystemClock = PrettyNum.userEntryToNumber(this._systemClock ?? '3.5G', -1);
      return PrettyNum.numberToUserDisplay(numericSystemClock * 0.5, 'Engineering', 8);
    }
  }

  _negotiateIsDebug(debug?: string): string {
    if (debug && debug === 'true') {
      return 'true';
    }
    return 'false';
  }

  _validateFtw(ftw: string, oneHzFtw: number, maxHzFtw: number): string {
    if (parseInt(ftw, 16) < Math.max(1, oneHzFtw)) {
      return Math.max(1, oneHzFtw).toString(16).toUpperCase();
    }
    if (parseInt(ftw, 16) > maxHzFtw) {
      return maxHzFtw.toString(16).toUpperCase();
    }
    return ftw.toUpperCase();
  }

  // Let the state begin
  get partData(): ProductRow | undefined {
    assert('get partData called before state service is ready', this._serviceState === 'Ready');
    return this.products.find((item) => {
      return item.name === this._partNumber;
    });
  }

  get finMoreRecentThanSClock(): boolean {
    assert('get finMoreRecentThanSClock called before state service is ready', this._serviceState === 'Ready');
    return this._finMoreRecentThanSClock ?? false;
  }
  set finMoreRecentThanSClock(value: boolean) {
    assert('set finMoreRecentThanSClock called before state service is ready', this._serviceState === 'Ready');
    this._finMoreRecentThanSClock = value;
  }

  get ftwMoreRecentThanTof(): boolean {
    assert('get ftwMoreRecentThanTof called before state service is ready', this._serviceState === 'Ready');
    return this._ftwMoreRecentThanTof ?? false;
  }
  set ftwMoreRecentThanTof(value: boolean) {
    assert('set ftwMoreRecentThanTof called before state service is ready', this._serviceState === 'Ready');
    this._ftwMoreRecentThanTof = value;
  }

  get partNumber(): string {
    assert('get partNumber called before state service is ready', this._serviceState === 'Ready');
    return this._partNumber ?? this._suppliedParms['partId'] ?? 'AD9914';
  }
  set partNumber(value: string) {
    assert('set partNumber called before state service is ready', this._serviceState === 'Ready');
    this._partNumber = value;
  }

  get multiplier(): number {
    assert('get multiplier called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._multiplier ?? this._suppliedParms['qmult'] ?? '1', 1);
  }
  set multiplier(value: number) {
    assert('set multiplier called before state service is ready', this._serviceState === 'Ready');
    this._multiplier = PrettyNum.numberToUserDisplay(value, 'Standard', 8);
  }

  get systemClock(): number {
    assert('get systemClock called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(
      this._systemClock ?? this._suppliedParms['qsclock'] ?? '3.5G',
      this._partData?.masterFclk ?? 3.5e9,
    );
  }
  set systemClock(value: number) {
    assert('set systemClock called before state service is ready', this._serviceState === 'Ready');
    this._systemClock = PrettyNum.numberToUserDisplay(value, 'Engineering', 12);
  }

  get inputClockFrequency(): number {
    assert('get inputClockFrequency called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(
      this._inputClockFrequency ?? this._suppliedParms['qfin'] ?? '2',
      this._partData?.masterFclk ?? 3.5e9,
    );
  }
  set inputClockFrequency(value: number) {
    assert('set inputClockFrequency called before state service is ready', this._serviceState === 'Ready');
    this._inputClockFrequency = PrettyNum.numberToUserDisplay(value, 'Engineering', 8);
  }

  get ftw(): number {
    assert('get ftw called before state service is ready', this._serviceState === 'Ready');
    return parseInt(this._ftw ?? this._suppliedParms['qftw'] ?? '', 16);
  }
  set ftw(value: number) {
    assert('set ftw called before state service is ready', this._serviceState === 'Ready');
    this._ftw = isNaN(value) ? '' : value.toString(16);
  }

  get targetFrequency(): number {
    assert('get targetFrequency called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._targetFrequency ?? this._suppliedParms['qtof'] ?? '1.28G', 1280000000);
  }
  set targetFrequency(value: number) {
    assert('set targetFrequency called before state service is ready', this._serviceState === 'Ready');
    this._targetFrequency = PrettyNum.numberToUserDisplay(sigFigsDown(value, 12), 'Engineering', 12);
  }

  get signalDisplayOptions(): string {
    assert('get signalDisplayOptions called before state service is ready', this._serviceState === 'Ready');
    return this._signalDisplayOptions ?? this._suppliedParms['qrso'] ?? '111111';
  }
  set signalDisplayOptions(value: string) {
    assert('set signalDisplayOptions called before state service is ready', this._serviceState === 'Ready');
    this._signalDisplayOptions = value;
  }

  get harmonicDB(): number {
    assert('get harmonicDB called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._harmonicDB ?? this._suppliedParms['harmonicDB'] ?? '-50', 0);
  }
  set harmonicDB(value: number) {
    assert('set harmonicDB called before state service is ready', this._serviceState === 'Ready');
    this._harmonicDB = PrettyNum.numberToUserDisplay(value, 'Standard', 8);
  }

  get lang(): string {
    assert('get lang called before state service is ready', this._serviceState === 'Ready');
    return this._suppliedParms['lang'] ?? 'en';
  }

  get isDebug(): boolean {
    assert('get isDebug called before state service is ready', this._serviceState === 'Ready');
    return this._isDebug && this._isDebug === 'true' ? true : false;
  }

  get displayFilters(): boolean {
    assert('get displayFilters called before state service is ready', this._serviceState === 'Ready');
    return this._displayFilters ?? PrettyNum.userEntryToNumber(this._suppliedParms['useFilters'] ?? '0', 0) !== 0;
  }
  set displayFilters(value: boolean) {
    assert('set displayFilters called before state service is ready', this._serviceState === 'Ready');
    this._displayFilters = value;
  }

  get filterType(): number {
    assert('get filterType called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._filterType ?? this._suppliedParms['qfType'] ?? '0', 0);
  }
  set filterType(value: number) {
    assert('set filterType called before state service is ready', this._serviceState === 'Ready');
    this._filterType = PrettyNum.numberToUserDisplay(value, 'Standard', 8);
  }

  get filterTopology(): number {
    assert('get filterTopology called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._filterTopology ?? this._suppliedParms['qfTop'] ?? '0', 0);
  }
  set filterTopology(value: number) {
    assert('set filterTopology called before state service is ready', this._serviceState === 'Ready');
    this._filterTopology = PrettyNum.numberToUserDisplay(value, 'Standard', 8);
  }

  get filterOrder(): number {
    assert('get filterOrder called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._filterOrder ?? this._suppliedParms['qfOrder'] ?? '1', 0);
  }
  set filterOrder(value: number) {
    assert('set filterOrder called before state service is ready', this._serviceState === 'Ready');
    this._filterOrder = PrettyNum.numberToUserDisplay(value, 'Standard', 8);
  }

  get fc(): number {
    assert('get fc called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._fc ?? this._suppliedParms['qfc'] ?? '1.75G', 0);
  }
  set fc(value: number) {
    assert('set fc called before state service is ready', this._serviceState === 'Ready');
    this._fc = PrettyNum.numberToUserDisplay(value, 'Engineering', 3);
  }

  get stopbandAtten(): number {
    assert('get stopbandAtten called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._stopbandAtten ?? this._suppliedParms['qfAtten'] ?? '60', 0);
  }
  set stopbandAtten(value: number) {
    assert('set stopbandAtten called before state service is ready', this._serviceState === 'Ready');
    this._stopbandAtten = PrettyNum.numberToUserDisplay(value, 'Standard', 8);
  }

  get passbandRipple(): number {
    assert('get passbandRipple called before state service is ready', this._serviceState === 'Ready');
    return PrettyNum.userEntryToNumber(this._passbandRipple ?? this._suppliedParms['qfRipple'] ?? '0.5', 0);
  }
  set passbandRipple(value: number) {
    assert('set passbandRipple called before state service is ready', this._serviceState === 'Ready');
    this._passbandRipple = PrettyNum.numberToUserDisplay(value, 'Standard', 8);
  }
}

declare module '@ember/service' {
  interface Registry {
    state: StateService;
  }
}
